// ========================================
// pages/Trustees.js - Trustee Management
// ========================================

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';

const Trustees = () => {
  const navigate = useNavigate();
  
  // Get user plan from localStorage
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const userPlan = user?.plan || 'free';
  const planLimits = {
    free: 2,
    premium: 3,
    business: 6,
    enterprise: 10
  };
  const maxTrustees = planLimits[userPlan] || 2;

  const [trustees, setTrustees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    relationship: 'other'
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchTrustees();
  }, []);

const fetchTrustees = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/trustees`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    // If response.data is an array, set it; otherwise set empty array
    setTrustees(Array.isArray(response.data) ? response.data : []);
    setError(''); // Clear any previous error
  } catch (err) {
    // Don't show error for empty trustees - just set empty array
    console.log('No trustees found or error fetching trustees');
    setTrustees([]);
    setError(''); // Clear error instead of showing "Failed to load trustees"
  } finally {
    setLoading(false);
  }
};

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      if (editingId) {
        // Update existing trustee - email is NOT sent to backend
        const updateData = {
          name: formData.name,
          phone: formData.phone,
          relationship: formData.relationship
        };
        await axios.put(`${process.env.REACT_APP_API_URL}/api/trustees/${editingId}`, updateData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        // Check limit before adding
        if (trustees.length >= maxTrustees) {
          setError(`Your ${userPlan} plan allows maximum ${maxTrustees} trustees.`);
          setSubmitting(false);
          return;
        }
        
        // Add new trustee
        await axios.post(`${process.env.REACT_APP_API_URL}/api/trustees`, formData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }

      setFormData({
        name: '',
        email: '',
        phone: '',
        relationship: 'other'
      });
      setShowForm(false);
      setEditingId(null);
      fetchTrustees();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save trustee');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (trustee) => {
    setFormData({
      name: trustee.name,
      email: trustee.email,
      phone: trustee.phone || '',
      relationship: trustee.relationship || 'other'
    });
    setEditingId(trustee.id);
    setShowForm(true);
    setError('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this trustee?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/trustees/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchTrustees();
    } catch (err) {
      alert('Failed to delete trustee');
    }
  };

  const handleResendInvite = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/trustees/${id}/resend`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert('Invitation resent successfully');
    } catch (err) {
      alert('Failed to resend invitation');
    }
  };

  const getRelationshipLabel = (value) => {
    const labels = {
      spouse: 'Spouse',
      child: 'Child',
      parent: 'Parent',
      sibling: 'Sibling',
      friend: 'Friend',
      lawyer: 'Lawyer',
      advisor: 'Advisor',
      other: 'Other'
    };
    return labels[value] || value;
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Trustees</h1>
        <div>
          <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
            ← Back to Dashboard
          </button>
        </div>
      </div>

      {/* Plan Info Banner */}
      <div style={styles.planBanner}>
        <div style={styles.planInfo}>
          <span style={styles.planName}>{userPlan?.toUpperCase()} PLAN</span>
          <span style={styles.planLimit}>
            {trustees.length} of {maxTrustees} trustees used
          </span>
        </div>
        <div style={styles.progressBar}>
          <div style={{
            ...styles.progressFill,
            width: `${(trustees.length / maxTrustees) * 100}%`,
            backgroundColor: '#FFD700'
          }} />
        </div>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {/* Add Button */}
      {!showForm && trustees.length < maxTrustees && (
        <div style={styles.addButtonContainer}>
          <button onClick={() => setShowForm(true)} style={styles.addButton}>
            + Add Trustee
          </button>
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div style={styles.formCard}>
          <h2 style={styles.formTitle}>
            {editingId ? 'Edit Trustee' : 'Add New Trustee'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                style={styles.input}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                style={styles.input}
                disabled={!!editingId}
                required={!editingId}
              />
              {editingId && (
                <small style={styles.hintText}>
                  Email cannot be edited for security. To change email, delete this trustee and add a new one.
                </small>
              )}
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="0244XXXXXX"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Relationship</label>
              <select
                name="relationship"
                value={formData.relationship}
                onChange={handleInputChange}
                style={styles.input}
              >
                <option value="spouse">Spouse</option>
                <option value="child">Child</option>
                <option value="parent">Parent</option>
                <option value="sibling">Sibling</option>
                <option value="friend">Friend</option>
                <option value="lawyer">Lawyer</option>
                <option value="advisor">Advisor</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div style={styles.formActions}>
              <button type="submit" style={styles.submitButton} disabled={submitting}>
                {submitting ? 'Saving...' : (editingId ? 'Update Trustee' : 'Add Trustee')}
              </button>
              <button 
                type="button" 
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    relationship: 'other'
                  });
                  setError('');
                }} 
                style={styles.cancelButton}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Trustees List */}
      {loading ? (
        <div style={styles.loading}>Loading trustees...</div>
      ) : (
        <div style={styles.trusteesList}>
          <h2 style={styles.sectionTitle}>Your Trustees</h2>
          
          {trustees.length === 0 ? (
            <div style={styles.emptyState}>
              <p>No trustees added yet.</p>
              <p style={styles.requiredNote}>
                Trustees are responsible for verifying your death.
              </p>
              <button onClick={() => setShowForm(true)} style={styles.emptyButton}>
                Add Your First Trustee
              </button>
            </div>
          ) : (
            trustees.map((trustee) => (
              <div key={trustee.id} style={styles.trusteeCard}>
                <div style={styles.trusteeHeader}>
                  <span style={styles.trusteeName}>{trustee.name}</span>
                  <span style={{
                    ...styles.trusteeStatus,
                    backgroundColor: trustee.isActive ? '#d1fae5' : '#fee2e2',
                    color: trustee.isActive ? '#065f46' : '#dc2626'
                  }}>
                    {trustee.isActive ? '✓ Active' : '⏳ Pending'}
                  </span>
                </div>
                
                <div style={styles.trusteeDetails}>
                  <p style={styles.trusteeEmail}>📧 {trustee.email}</p>
                  {trustee.phone && <p style={styles.trusteePhone}>📞 {trustee.phone}</p>}
                  <p style={styles.trusteeRelationship}>
                    🔗 {getRelationshipLabel(trustee.relationship)}
                  </p>
                  <p style={styles.trusteeDate}>
                    Added: {new Date(trustee.invitedAt).toLocaleDateString()}
                  </p>
                  {trustee.verifiedDeath && (
                    <p style={styles.verifiedBadge}>
                      ✅ Verified death on {new Date(trustee.verifiedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>

                <div style={styles.trusteeActions}>
                  {!trustee.isActive && (
                    <button
                      onClick={() => handleResendInvite(trustee.id)}
                      style={styles.resendButton}
                    >
                      Resend Invite
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(trustee)}
                    style={styles.editButton}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(trustee.id)}
                    style={styles.deleteButton}
                  >
                    Remove
                  </button>
                </div>

                {/* Show invitation link for testing */}
                {!trustee.isActive && trustee.invitationLink && (
                  <div style={styles.inviteLinkBox}>
                    <small>Test link: </small>
                    <a 
                      href={trustee.invitationLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.inviteLink}
                    >
                      {trustee.invitationLink}
                    </a>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Info Box about Trustee Role */}
      <div style={styles.infoBox}>
        <h3 style={styles.infoTitle}>🔐 What Trustees Do</h3>
        <p style={styles.infoText}>
          Trustees verify your death when you miss check-ins for 90 days. 
          They do not see your assets - they only confirm you are deceased.
        </p>
        <p style={styles.infoText}>
          After verification, your Next of Kin get access to your vault.
        </p>
        <p style={styles.infoNote}>
          Your plan allows up to {maxTrustees} trustees.
        </p>
        <p style={styles.infoNote}>
          <strong>Note:</strong> Trustee email cannot be edited for security. To change email, delete the trustee and add a new one.
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5F0E6',
    padding: '20px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    maxWidth: '800px',
    margin: '0 auto 30px'
  },
  title: {
    fontSize: '28px',
    color: '#1A1A1A',
    margin: 0
  },
  backButton: {
    padding: '10px 20px',
    backgroundColor: '#1A1A1A',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  planBanner: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '10px',
    marginBottom: '30px',
    maxWidth: '800px',
    margin: '0 auto 30px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  planInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  planName: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#FFD700'
  },
  planLimit: {
    fontSize: '14px',
    color: '#666'
  },
  progressBar: {
    height: '8px',
    backgroundColor: '#ddd',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    transition: 'width 0.3s ease'
  },
  errorBox: {
    backgroundColor: '#fee2e2',
    border: '1px solid #ef4444',
    color: '#dc2626',
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '20px',
    maxWidth: '800px',
    margin: '0 auto 20px'
  },
  addButtonContainer: {
    maxWidth: '800px',
    margin: '0 auto 20px',
    textAlign: 'right'
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  formCard: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '10px',
    maxWidth: '500px',
    margin: '0 auto 30px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  },
  formTitle: {
    fontSize: '18px',
    color: '#FFD700',
    marginBottom: '20px'
  },
  formGroup: {
    marginBottom: '15px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#1A1A1A',
    marginBottom: '5px'
  },
  input: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    fontSize: '14px'
  },
  hintText: {
    fontSize: '11px',
    color: '#666',
    marginTop: '5px',
    display: 'block'
  },
  formActions: {
    display: 'flex',
    gap: '10px',
    marginTop: '20px'
  },
  submitButton: {
    flex: '1',
    padding: '10px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    color: '#666',
    border: '1px solid #666',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
    maxWidth: '800px',
    margin: '0 auto'
  },
  trusteesList: {
    maxWidth: '800px',
    margin: '0 auto'
  },
  sectionTitle: {
    fontSize: '20px',
    color: '#2E7D32',
    marginBottom: '20px'
  },
  emptyState: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '10px',
    textAlign: 'center',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  requiredNote: {
    color: '#666',
    fontSize: '14px',
    marginBottom: '20px'
  },
  emptyButton: {
    padding: '12px 30px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '5px',
    fontSize: '16px',
    cursor: 'pointer'
  },
  trusteeCard: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '10px',
    marginBottom: '15px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    position: 'relative'
  },
  trusteeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  trusteeName: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#1A1A1A'
  },
  trusteeStatus: {
    padding: '4px 8px',
    borderRadius: '3px',
    fontSize: '12px'
  },
  trusteeDetails: {
    marginBottom: '15px'
  },
  trusteeEmail: {
    fontSize: '14px',
    color: '#1A1A1A',
    margin: '5px 0'
  },
  trusteePhone: {
    fontSize: '14px',
    color: '#1A1A1A',
    margin: '5px 0'
  },
  trusteeRelationship: {
    fontSize: '14px',
    color: '#666',
    margin: '5px 0',
    textTransform: 'capitalize'
  },
  trusteeDate: {
    fontSize: '12px',
    color: '#999',
    margin: '5px 0'
  },
  verifiedBadge: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
    padding: '5px 10px',
    borderRadius: '3px',
    fontSize: '12px',
    display: 'inline-block',
    marginTop: '5px'
  },
  trusteeActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    borderTop: '1px solid #eee',
    paddingTop: '15px'
  },
  resendButton: {
    padding: '5px 10px',
    backgroundColor: '#2E7D32',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  editButton: {
    padding: '5px 10px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  deleteButton: {
    padding: '5px 10px',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  inviteLinkBox: {
    marginTop: '10px',
    padding: '10px',
    backgroundColor: '#f3f4f6',
    borderRadius: '5px',
    fontSize: '12px',
    wordBreak: 'break-all'
  },
  inviteLink: {
    color: '#2E7D32',
    textDecoration: 'none'
  },
  infoBox: {
    backgroundColor: '#e8f4fd',
    border: '1px solid #2E7D32',
    borderRadius: '10px',
    padding: '20px',
    maxWidth: '800px',
    margin: '30px auto 0'
  },
  infoTitle: {
    fontSize: '16px',
    color: '#1A1A1A',
    marginBottom: '10px'
  },
  infoText: {
    fontSize: '14px',
    color: '#1A1A1A',
    marginBottom: '10px',
    lineHeight: '1.5'
  },
  infoNote: {
    fontSize: '14px',
    color: '#2E7D32',
    fontWeight: 'bold',
    marginTop: '10px'
  }
};

export default Trustees;